import React, { useContext, useState, memo } from 'react';
import { useImmer } from 'use-immer';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Paper, FormControlLabel } from '@mui/material';
import { SegmentedTabs } from './Subtabs';
import { LoadError, Loading } from './useApiData';
import SelectInput from './SelectInput';
import { apiGetPrepaidCompetitionSync, apiGetPrepaidCompetitionPaymentEvents, apiSetPrepaidHideInSync, PaymentEvent } from './Api';
import { reportError } from './CallCenter';
import { PrepaidCompetitionSyncData, SyncRow, SyncStatus } from './Types';
import formatCurrency from './Formatting';
import PrepaidCompetitionConfigEditor from './PrepaidCompetitionConfig'
import PrepaidServices, { FocusClub } from './PrepaidServices';
import { RouteContext, RoutePrefixContext } from './route';
import PrepaidCompetitionReconciliation from './PrepaidCompetitionReconciliation';
import { CourierBand, CourierRun, CourierWaiting, useCourier } from './Courier';

// Human-readable label + colour per reconciliation status. green = nothing to do,
// blue = an action is available, amber = a partial payment is waiting to be registered
// (stands apart from a full one), red = an anomaly to look at.
const STATUS: Record<SyncStatus, { label: string, color: string }> = {
    in_sync:              { label: 'I synk',                     color: '#2e7d32' },
    eventor_only:         { label: 'Endast i Eventor',           color: '#1565c0' },
    payment_due:          { label: 'Betalning att registrera',   color: '#1565c0' },
    partial_payment_due:  { label: 'Delbetalning att registrera', color: '#ed6c02' },
    amount_drift:         { label: 'Beloppsavvikelse',           color: '#c62828' },
    visma_only:           { label: 'Endast i Visma',             color: '#c62828' },
    overpaid_in_eventor:  { label: 'Mer betalt i Eventor',       color: '#c62828' },
}

// Outcomes that don't map to a clean new row state are shown as a short note in the
// status column (the clean ones — imported / registered — update the row instead).
const NOTE: Record<string, { label: string, color: string }> = {
    skipped:        { label: 'Hoppades över',              color: '#666' },
    bad_amounts:    { label: 'Okänt belopp',               color: '#c62828' },
    import_failed:  { label: 'Import misslyckades',        color: '#c62828' },
}

const EPS = 0.005
// An order for nothing. Eventor issues these — a free entry, an order whose rows were all removed —
// and there is no voucher to be made from a total of zero: the import would reach Visma with no
// income rows and be refused. Offering it for import would be offering work that cannot be done.
const isZeroAmount = (r: SyncRow) => r.eventor_amount !== null && Math.abs(r.eventor_amount) < EPS
// Zero rows never reach here: they are filtered out before the table sees them.
const isImportable = (r: SyncRow) => r.sync_status === 'eventor_only'
const isPayable = (r: SyncRow) => r.sync_status === 'payment_due' || r.sync_status === 'partial_payment_due'
const isActionable = (r: SyncRow) => isImportable(r) || isPayable(r)

const amount = (v: number | null) => v === null ? '–' : formatCurrency(v)
// A payment is partial when something — but not the whole total — has been collected. Measured
// towards the total rather than upwards, since a credit's amounts are negative: half of -800 kr
// collected is -400, which is neither "more than zero" nor "less than the total".
const isPartial = (paid: number | null, total: number | null) => {
    if (paid === null || total === null) return false
    const towards = total < 0 ? -1 : 1
    return paid * towards > EPS && (total - paid) * towards > EPS
}

// Mirror of sync_prepaid_competition._status: recompute a row's status from its fields after we
// apply an action locally, so the new status (e.g. 'I synk') shows without a reload.
const computeStatus = (r: SyncRow): SyncStatus => {
    const eoPaid = r.eventor_paid_amount ?? 0, viPaid = r.visma_paid_amount ?? 0
    // Every comparison is taken towards the total, so a credit — negative total, negative payment
    // handing money back — follows the same rules instead of needing a status of its own.
    const total = r.eventor_amount ?? r.visma_amount ?? 0
    const towards = total < 0 ? -1 : 1
    // Eventor recording more than Visma is an anomaly even if absent from Visma.
    if ((eoPaid - viPaid) * towards > EPS) return 'overpaid_in_eventor'
    if (r.eventor_amount !== null && r.visma_amount === null) return 'eventor_only'
    if (r.eventor_amount === null && r.visma_amount !== null) return 'visma_only'
    if (Math.abs((r.eventor_amount ?? 0) - (r.visma_amount ?? 0)) > EPS) return 'amount_drift'
    if ((viPaid - eoPaid) * towards > EPS) {
        return Math.abs(viPaid) >= Math.abs(r.visma_amount ?? 0) - EPS
            ? 'payment_due' : 'partial_payment_due'
    }
    return 'in_sync'
}

// A paid-amount cell. When a partial payment has landed (some but not all of the total),
// it's amber + bold so partially paid rows jump out even when otherwise quiet.
const PaidCell = ({ paid, total }: { paid: number | null, total: number | null }) => {
    if (paid === null) return <TableCell align="right">–</TableCell>
    const partial = isPartial(paid, total)
    return (
        <TableCell align="right"
            style={partial ? { color: '#ed6c02', fontWeight: 600 } : undefined}
            title={partial && total !== null ? `Delbetald: ${formatCurrency(paid)} av ${formatCurrency(total)}` : undefined}>
            {formatCurrency(paid)}
        </TableCell>
    )
}

const Row = ({ row, checked, onToggle, disabled, note }: {
    row: SyncRow, checked: boolean, onToggle: () => void, disabled: boolean, note: string | undefined,
}) => {
    const s = STATUS[row.sync_status]
    // Status column: a spinner while this row's action runs, then either the recomputed
    // status (clean outcome) or a short note (an outcome we can't recompute from).
    let statusCell: React.ReactNode
    if (note === 'pending') statusCell = <CircularProgress size="1em"/>
    else if (note !== undefined) statusCell = <span style={{ color: NOTE[note]?.color ?? '#666', fontSize: '0.85em' }}>{NOTE[note]?.label ?? note}</span>
    else statusCell = (
        <>
            <span style={{ color: s.color, fontWeight: 600 }}>{s.label}</span>
        </>
    )
    return (
        <TableRow hover>
            <TableCell padding="checkbox" align="center">
                {isActionable(row) && <Checkbox size="small" checked={checked} disabled={disabled} onChange={onToggle}/>}
            </TableCell>
            <TableCell>{row.reference_number}</TableCell>
            <TableCell>{row.org_name ?? '–'}</TableCell>
            <TableCell align="right">{amount(row.eventor_amount)}</TableCell>
            <TableCell align="right">{amount(row.visma_amount)}</TableCell>
            <PaidCell paid={row.eventor_paid_amount} total={row.eventor_amount}/>
            <PaidCell paid={row.visma_paid_amount} total={row.visma_amount}/>
            <TableCell>{statusCell}</TableCell>
        </TableRow>
    )
}

// `active` is no longer used: nothing here pre-loads on activation, because every read of Eventor
// now costs the user a click in another tab and must be asked for.
// Both views act on the same competition, through the same courier, and only one of them can be
// running a job at a time — so all three come from above rather than being owned by either.
type ViewProps = {
    paymentEventId: number | null,
    courier: CourierRun,
    running: string | null,
    setRunning: (what: string | null) => void,
}


const SyncView = memo(({ paymentEventId, courier, running, setRunning }: ViewProps) => {
    // The sync view logs into Eventor and scrapes — slow — so fetch on demand, not on
    // mount. First click loads; afterwards the button re-loads.
    // The payment event is picked live (not stored). Its options are scraped (slow), so we load
    // them the first time the tab is shown (not on app load — every tab is mounted from the start)
    // and useApiData keeps the result cached for the session. The sync fetch is keyed on the event.
    // Reading Eventor now costs the user a click in another tab (see Courier.tsx), so nothing here
    // fetches on its own any more — not on mount, not on activation, not on opening the select. One
    // button, one job, one tab, and it collects both the competitions and the rows.
    // Fetched inside the courier job rather than by an effect keyed on its token. The effect
    // version looked tidier and was racy: the job ends when its callback returns, so a callback
    // that only set state finished the job *before* the fetch it was meant to cover, leaving the
    // request waiting on a browser that had already been told to stop. It worked whenever the
    // effect happened to run first, which is why it worked at all.
    const [data, setData] = useImmer<PrepaidCompetitionSyncData | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<unknown>(null)
    const [selected, setSelected] = useState<Set<number>>(new Set())
    const [busy, setBusy] = useState(false)                          // an import/register action is running
    const [notes, setNotes] = useState<Record<number, string>>({})   // per-row 'pending' / leftover-note status

    // Off until a load says otherwise, so the table starts by showing everything rather than
    // hiding rows on a guess.
    const [hideInSync, setHideInSync] = useState(false)
    const toggleHideInSync = () => {
        const next = !hideInSync
        setHideInSync(next)
        // Fire-and-forget, like the other view preferences: it is a preference, and a failed write
        // just means the next load shows the stored value again.
        void apiSetPrepaidHideInSync(next).catch(() => {})
    }

    // Orders for nothing are left out entirely. Eventor issues them — a free entry, an order whose
    // rows were all removed — and marks them paid the moment they are created; Visma will not accept
    // a zero invoice at all. So there is no action for one, now or ever, and a row nobody may touch
    // is only something to scroll past.
    const allRows = data?.rows ?? []
    const rows = allRows.filter(r => !isZeroAmount(r))
    const zeroCount = allRows.length - rows.length
    // Rows that are done and say nothing further. A row acted on in this run keeps its note, so it
    // stays put long enough to be read rather than vanishing the instant it succeeds.
    const isSettled = (r: SyncRow) => r.sync_status === 'in_sync' && notes[r.reference_number] === undefined
    const hiddenCount = hideInSync ? rows.filter(isSettled).length : 0
    const visibleRows = hideInSync ? rows.filter(r => !isSettled(r)) : rows
    const toggle = (ref: number) => setSelected(s => {
        const n = new Set(s); n.has(ref) ? n.delete(ref) : n.add(ref); return n
    })
    const selectedOf = (pred: (r: SyncRow) => boolean) =>
        rows.filter(r => pred(r) && selected.has(r.reference_number)).map(r => r.reference_number)
    const importRefs = selectedOf(isImportable)
    const payRefs = selectedOf(isPayable)

    // Header "select all" over the actionable rows (the only ones that get a checkbox).
    // Both action buttons filter to their own kind, so selecting everything is harmless.
    const actionableRefs = rows.filter(isActionable).map(r => r.reference_number)
    const allSelected = actionableRefs.length > 0 && actionableRefs.every(ref => selected.has(ref))
    const someSelected = actionableRefs.some(ref => selected.has(ref))
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(actionableRefs))

    const refresh = () => {
        if (paymentEventId === null) return
        setNotes({})
        setLoading(true)
        setError(null)
        setRunning('Synkvy')
        // Every refresh is its own job, tab and click. A job cannot be reused: it is finished when
        // its work is done, and the browser-side script stops with it.
        void courier.run(async () => {
            try {
                const fetched = await apiGetPrepaidCompetitionSync(paymentEventId)
                setData(fetched)
                // Applied on a load, never on a row edit: `data` is Immer state and changes
                // identity whenever a row is updated, so following it would reset the checkbox to
                // whatever the last fetch happened to say, undoing a toggle made since.
                setHideInSync(fetched.hide_in_sync)
            } catch (e: unknown) {
                setError(e)          // already on the error bus; this drives the retry affordance
            } finally {
                setLoading(false)
                setRunning(null)
            }
        })
    }

    const beginAction = (what: string, refs: number[]) => {
        setBusy(true)
        setRunning(what)
        setSelected(new Set())
        setNotes(Object.fromEntries(refs.map(r => [r, 'pending'])))
    }
    const clearNote = (ref: number) => setNotes(n => { const m = { ...n }; delete m[ref]; return m })
    const dropPending = () => setNotes(n => Object.fromEntries(Object.entries(n).filter(([, v]) => v !== 'pending')))

    // Apply an action to a row locally and recompute its status, so the view stays
    // truthful without re-scraping Eventor.
    const updateRow = (ref: number, mutate: (r: SyncRow) => void) => setData(draft => {
        if (!draft) return
        const row = draft.rows.find(r => r.reference_number === ref)
        if (row) { mutate(row); row.sync_status = computeStatus(row) }
    })

    // Import (Eventor -> Visma): SSE progress stream; each row resolves as its event arrives.
    const importSelected = () => {
        if (importRefs.length === 0 || paymentEventId === null) return
        beginAction('Import', importRefs)
        // The job has to outlive the whole stream, not just the request that starts it: the server
        // fetches from Eventor for every invoice as it goes. So the courier's work is a promise
        // that settles when the stream ends, however it ends — resolving early would finish the
        // job mid-import and strand every fetch still to come.
        void courier.run(() => new Promise<void>(done => {
        const url = new URL('../incomes/prepaid-competition/import', document.baseURI)
        url.searchParams.append('payment_event_id', String(paymentEventId))
        url.searchParams.append('refs', importRefs.join(','))
        const source = new EventSource(url)
        source.addEventListener('updateImportStatus', (e: MessageEvent<string>) => {
            const d = JSON.parse(e.data)
            const ref: number = d.invoice_number
            if (d.import_status === 'imported_now' || d.import_status === 'already_imported') {
                // Now present in Visma at the Eventor amount, nothing collected yet -> recompute.
                updateRow(ref, r => { r.visma_amount = r.eventor_amount; r.visma_paid_amount = 0 })
                clearNote(ref)
            } else {
                setNotes(n => ({ ...n, [ref]: d.import_status }))  // skipped / bad_amounts / import_failed
            }
        })
        source.addEventListener('finished', () => { source.close(); setBusy(false); setRunning(null); dropPending(); done() })
        source.addEventListener('importError', (e: MessageEvent<string>) => {
            source.close(); setBusy(false); setRunning(null); dropPending(); done()
            reportError({ status: 502, code: 'import_failed', message: JSON.parse(e.data).message })
        })
        source.onerror = () => {
            source.close(); setBusy(false); setRunning(null); dropPending(); done()
            reportError({ status: 0, code: 'import_connection_lost', message: 'Anslutningen till importen bröts.' })
        }
        }))
    }

    // Reverse-sync (Visma -> Eventor): SSE, each row resolving as its payment is registered.
    // Every payment is a separate submission that cannot be batched, so a season's worth takes
    // minutes — and if it stops half-way, which ones went through is the whole question. Rows that
    // were never reached simply lose their pending marker, which is the truth about them.
    const registerPaymentsSelected = () => {
        if (payRefs.length === 0 || paymentEventId === null) return
        beginAction('Betalningar', payRefs)
        // The only thing Eko writes into Eventor, and it reads the form first to carry the
        // anti-forgery token back — so it needs the courier just as the reads do, and the job must
        // outlive the stream rather than the request that opens it.
        void courier.run(() => new Promise<void>(done => {
        const url = new URL('../incomes/prepaid-competition/registerpayments', document.baseURI)
        url.searchParams.append('payment_event_id', String(paymentEventId))
        url.searchParams.append('refs', payRefs.join(','))
        const source = new EventSource(url)
        const stop = () => { source.close(); setBusy(false); setRunning(null); dropPending(); done() }
        source.addEventListener('paymentRegistered', (e: MessageEvent<string>) => {
            // The delta was registered, so Eventor is now level with Visma -> recompute ('I synk').
            const ref: number = JSON.parse(e.data).reference_number
            updateRow(ref, r => { r.eventor_paid_amount = r.visma_paid_amount })
            clearNote(ref)
        })
        source.addEventListener('paymentSkipped', (e: MessageEvent<string>) => {
            const ref: number = JSON.parse(e.data).reference_number
            setNotes(n => ({ ...n, [ref]: 'skipped' }))
        })
        source.addEventListener('finished', stop)
        source.addEventListener('paymentError', (e: MessageEvent<string>) => {
            stop()
            reportError({ status: 502, code: 'register_payments_failed', message: JSON.parse(e.data).message })
        })
        source.onerror = () => {
            stop()
            reportError({ status: 0, code: 'register_payments_connection_lost',
                          message: 'Anslutningen till betalningsregistreringen bröts.' })
        }
        }))
    }

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: '0.75rem' }}>
            <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '0.5rem', alignItems: 'center' }}>
                <Button variant="outlined" disabled={loading || busy || running !== null || paymentEventId === null} onClick={refresh}>
                    {data !== null ? 'Uppdatera synkvyn' : 'Hämta synkvy'}
                </Button>
                {data !== null &&
                    <>
                        <Button variant="contained" disabled={loading || busy || running !== null || importRefs.length === 0} onClick={importSelected}>
                            Importera valda → Visma ({importRefs.length})
                        </Button>
                        <Button variant="contained" disabled={loading || busy || running !== null || payRefs.length === 0} onClick={registerPaymentsSelected}>
                            Registrera betalningar → Eventor ({payRefs.length})
                        </Button>
                    </>
                }
                {/* On a reload the table is already shown; a spinner in this row gives
                    feedback without shifting the table the way a block loader would. */}
                {loading && data !== null && <CircularProgress size="1.2em"/>}
                {data !== null &&
                    <FormControlLabel
                        control={<Checkbox size="small" checked={hideInSync}
                                           onChange={toggleHideInSync}/>}
                        label={<span style={{ fontSize: '0.9em' }}>
                            Dölj rader i synk{hiddenCount > 0 && ` (${hiddenCount})`}
                        </span>}/>}
                {/* Said rather than silently dropped: a count that does not add up is worse than
                    one that is explained. */}
                {zeroCount > 0 &&
                    <span style={{ fontSize: '0.85em', color: '#666' }}>
                        {zeroCount} nollfakturor visas inte
                    </span>}
            </div>
            {loading && data === null && <Loading/>}
            {error !== null && !loading && <LoadError onRetry={refresh}/>}
            {data !== null && rows.length === 0 && !loading && <div>Inga ordrar.</div>}
            {/* Everything is done — worth saying, since the alternative is an empty table that
                looks like a failed fetch. */}
            {data !== null && rows.length > 0 && visibleRows.length === 0 && !loading &&
                <div style={{ color: '#2e7d32' }}>Allt är i synk.</div>}
            {data !== null && visibleRows.length > 0 &&
                <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" align="center">
                                    <Checkbox size="small"
                                        checked={allSelected}
                                        indeterminate={someSelected && !allSelected}
                                        disabled={busy || loading || actionableRefs.length === 0}
                                        onChange={toggleAll}
                                        inputProps={{ 'aria-label': 'Markera alla' }}/>
                                </TableCell>
                                <TableCell>Referensnummer</TableCell>
                                <TableCell>Klubb</TableCell>
                                <TableCell align="right">Belopp (Eventor)</TableCell>
                                <TableCell align="right">Belopp (Visma)</TableCell>
                                <TableCell align="right">Betalt (Eventor)</TableCell>
                                <TableCell align="right">Betalt (Visma)</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map(row =>
                                <Row key={row.reference_number} row={row}
                                    checked={selected.has(row.reference_number)}
                                    onToggle={() => toggle(row.reference_number)}
                                    disabled={busy || loading}
                                    note={notes[row.reference_number]}/>)}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </div>
    )
})

// The Förskottstävlingar area: choose the competition once, then choose what to do with it.
//
// The selector, the courier and the one-job-at-a-time rule live here rather than in either view,
// because both views are about the same competition and neither owns it. They used to sit one above
// the other in a single panel, which put two similarly-named fetch buttons in one row and stacked
// two result areas with nothing to say how they related.
const PrepaidCompetitionSync = ({ active }: { active?: boolean }) => {
    const courier = useCourier()
    const [events, setEvents] = useState<PaymentEvent[] | null>(null)
    const [paymentEventId, setPaymentEventId] = useState<number | null>(null)
    // What the courier is busy with, if anything. One job at a time, so whichever view is running
    // holds the other's buttons — comprehensible only if something says what is going on.
    const [running, setRunning] = useState<string | null>(null)
    // A club handed over from the reconciliation, so Tjänster opens on it. Cleared once
    // that view has taken it, so returning to the tab later starts fresh.
    const [focusClub, setFocusClub] = useState<FocusClub | null>(null)
    // Moving to a sibling tab is a navigation, not local state: the sub-tab level is driven
    // by the hash, so Back works and a reload lands where you were.
    const { navigate } = useContext(RouteContext)
    const prefix = useContext(RoutePrefixContext)
    const goToServices = () => navigate([...(prefix ? prefix.split('/') : []), 'tjanster'])

    // The competitions come from Eventor too, so they ride along in the same job rather than
    // costing their own tab. Loaded once and kept: the list does not change during a session.
    const loadEvents = () => courier.run(async () => {
        setRunning('Hämtning av tävlingar')
        try {
            // ?? [] rather than trusting the field: a response without it would otherwise leave
            // `events` undefined, which is neither null nor a list -- the picker would be empty and
            // the button gone, which is exactly what an unexplained failure looked like.
            setEvents((await apiGetPrepaidCompetitionPaymentEvents()).payment_events ?? [])
        } finally {
            setRunning(null)
        }
    }).catch(() => {})       // reported by the error bus; nothing further to do here

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', gap: '0.75rem' }}>
            <div style={{ width: '100%', maxWidth: '60rem' }}>
                <CourierBand/>
                <CourierWaiting waiting={courier.needsClick}/>
            </div>
            <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '0.5rem', alignItems: 'center' }}>
                <SelectInput<number, PaymentEvent, never> label="Förskottstävling" required={false}
                    sx={{ minWidth: 300 }}
                    options={events ?? []}
                    valueGetter={e => e.id} nameGetter={e => e.name}
                    value={paymentEventId} onChange={setPaymentEventId}/>
                {/* Offered again whenever there is nothing to choose from, not only before the
                    first attempt. A fetch that came back empty used to take the button with it and
                    say nothing, so the only way to try again was to reload Eko. */}
                {(events === null || events.length === 0) &&
                    <Button variant="outlined" disabled={courier.needsClick || running !== null}
                            onClick={() => void loadEvents()}>
                        Hämta tävlingar
                    </Button>}
                {events !== null && events.length === 0 && running === null &&
                    <span style={{ color: '#b26a00', fontSize: '0.9em' }}>
                        Eventor listade inga förskottstävlingar. Kontrollera att du är inloggad i
                        Eventor-fliken som en person med arrangörsrättigheter.
                    </span>}
                {running !== null &&
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                                   color: '#555', fontSize: '0.9em' }}>
                        <CircularProgress size="1em"/> {running} pågår
                    </span>}
            </div>
            <SegmentedTabs active={active} panels={[
                { slug: 'fakturor', label: 'Fakturor och betalningar',
                  panel: () => <SyncView paymentEventId={paymentEventId} courier={courier}
                                         running={running} setRunning={setRunning}/> },
                { slug: 'avstamning', label: 'Avstämning mot anmälningar',
                  panel: () => <PrepaidCompetitionReconciliation
                                   paymentEventId={paymentEventId} courier={courier}
                                   running={running} setRunning={setRunning}
                                   onCorrectClub={club => { setFocusClub(club); goToServices() }}/> },
                { slug: 'tjanster', label: 'Tjänster',
                  panel: () => <PrepaidServices paymentEventId={paymentEventId} courier={courier}
                                                running={running} setRunning={setRunning}
                                                focusClub={focusClub}
                                                onFocused={() => setFocusClub(null)}/> },
                { slug: 'installningar', label: 'Inställningar',
                  panel: () => <PrepaidCompetitionConfigEditor/> },
            ]}/>
        </div>
    )
}

export default PrepaidCompetitionSync
